import { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getUserSettings, updateUserSetting } from "services";

import { useIsAuthenticated } from "utils";

import { GET_USER_SETTINGS, SOIL_SIGHT_ONBOARDING_SETTING, UPDATE_USER_SETTING } from "consts";

import { UserSettings } from "models";

import {
  SoilSightOnboardingState,
  mergeInProgressDraft,
  parseSoilSightOnboardingState,
  serializeSoilSightOnboardingState,
} from "pages/app/scenario-onboarding/utils/onboardingState";

export const useGetSoilSightOnboardingSetting = () => {
  const isAuthenticated = useIsAuthenticated();

  const query = useQuery<UserSettings>({
    queryKey: [GET_USER_SETTINGS],
    queryFn: ({ signal }) => getUserSettings({ signal }),
    staleTime: 1000 * 60 * 5,
    enabled: isAuthenticated,
  });

  // Memoized on the raw string: JSON.parse on every render of every consumer (gates,
  // autosave, restore, import hooks) would also break referential equality for their deps.
  const onboardingState = useMemo(() => parseSoilSightOnboardingState(query.data?.soilSightOnboarding), [query.data?.soilSightOnboarding]);

  return {
    onboardingState,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

/** Small patches of the in-progress draft. */
export const useSoilSightOnboardingSettingPatch = () => {
  const { onboardingState } = useGetSoilSightOnboardingSetting();
  const { mutate } = useUpdateSoilSightOnboardingSetting();

  const patchImportBannerDismissed = () => {
    if (onboardingState?.status !== "in-progress") return;
    mutate(mergeInProgressDraft(onboardingState, { farmId: onboardingState.farmId, importBannerDismissed: true }));
  };

  // Once the landing was passed, later entries resume at step 1.
  const patchLandingSeen = () => {
    if (onboardingState?.status !== "in-progress" || onboardingState.landingSeen === true) return;
    mutate(mergeInProgressDraft(onboardingState, { farmId: onboardingState.farmId, landingSeen: true }));
  };

  return { patchImportBannerDismissed, patchLandingSeen };
};

export const useUpdateSoilSightOnboardingSetting = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();

  return useMutation({
    mutationKey: [UPDATE_USER_SETTING, SOIL_SIGHT_ONBOARDING_SETTING],
    mutationFn: (state: SoilSightOnboardingState) => {
      if (!isAuthenticated) return Promise.reject(new Error("Not Authorized"));
      return updateUserSetting({ item: SOIL_SIGHT_ONBOARDING_SETTING, value: serializeSoilSightOnboardingState(state) });
    },
    onSuccess: (_data, state) => {
      // Reflect the new state immediately so route gates flip without waiting for a refetch.
      queryClient.setQueryData<UserSettings>([GET_USER_SETTINGS], (previous) =>
        previous ? { ...previous, soilSightOnboarding: serializeSoilSightOnboardingState(state) } : previous
      );
      queryClient.invalidateQueries({ queryKey: [GET_USER_SETTINGS] });
    },
  });
};
